import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {HeadingNode} from '@lexical/rich-text';
import {useEffect} from 'react';

import {ChevronPlugin} from './ChevronPlugin';
import {CollapsePlugin} from './CollapsePlugin';
import {$convertToEnhancedHeadingNode, EnhancedHeadingNode} from './EnhancedHeadingNode';
import {GuidancePlugin} from './GuidancePlugin';
import {HeadingControlsPlugin} from './HeadingControlsPlugin';
import {HeadingEnterPlugin} from './HeadingEnterPlugin';
import {setEditorRole, setHeadingType, updateHeadingNodeRegistry} from './HeadingNodeUtils';
import {HeadingPastePlugin} from './HeadingPastePlugin';
import {HeadingProtectionPlugin} from './HeadingProtectionPlugin';
import {useIsTemplateMode} from './RoleContext';
import {WordCountPlugin} from './WordCountPlugin';

export type EnhancedHeadingType = 'enabled' | 'collapsible' | undefined;

interface EnhancedHeadingPluginProps {
  enhancedHeadings?: EnhancedHeadingType;
}

/**
 * Enhanced Heading Plugin that orchestrates multiple sub-plugins
 * for different enhanced heading functionality.
 */
export const EnhancedHeadingPlugin = ({enhancedHeadings}: EnhancedHeadingPluginProps) => {
  const [editor] = useLexicalComposerContext();
  const isTemplateMode = useIsTemplateMode();

  useEffect(() => {
    if (enhancedHeadings === undefined) {
      return;
    }

    // Set the editor role in module state for nodes to access during creation
    setHeadingType(enhancedHeadings);
    setEditorRole(isTemplateMode ? 'template' : 'document');

    // Initial setup - update registry
    updateHeadingNodeRegistry(editor);

    // Convert regular HeadingNode to EnhancedHeadingNode (only in template mode)
    const removeHeadingTransform = editor.registerNodeTransform(HeadingNode, (node) => {
      if (isTemplateMode && !(node instanceof EnhancedHeadingNode)) {
        const collapsibleNode = $convertToEnhancedHeadingNode(node);
        node.replace(collapsibleNode);
      }
    });

    // Handle when headings are created or changed - update registry and remove empty headings in template mode
    const removeNodeTransform = editor.registerNodeTransform(EnhancedHeadingNode, (node) => {
      updateHeadingNodeRegistry(editor);

      // In template mode, remove empty headings
      if (isTemplateMode && !node.getTextContent().trim()) {
        node.remove();
      }
    });

    return () => {
      removeHeadingTransform();
      removeNodeTransform();
    };
  }, [editor, enhancedHeadings, isTemplateMode]);

  // Render sub-plugins based on enhancedHeadings mode
  return (
    <>
      <HeadingPastePlugin />
      {enhancedHeadings === 'enabled' ? (
        <>
          {/* Core functionality plugins */}
          <ChevronPlugin />
          <HeadingProtectionPlugin />
          <WordCountPlugin isTemplateMode={isTemplateMode} />
          <HeadingControlsPlugin isTemplateMode={isTemplateMode} />
          <GuidancePlugin />
          <CollapsePlugin />
        </>
      ) : enhancedHeadings === 'collapsible' ? (
        <>
          {/* Basic collapsible functionality */}
          <ChevronPlugin />
          <CollapsePlugin />
        </>
      ) : null}
      <HeadingEnterPlugin />
    </>
  );
};
